import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useRef,
} from 'react'
import { useLocation } from 'react-router-dom'
import { removeHubspotScripts } from '../../utils/helper-functions'

interface HubspotContextType {
    isHubspotEnabled: boolean
    enableHubspot: () => void
    disableHubspot: () => void
}

const HubspotContext = createContext<HubspotContextType | undefined>(undefined)

export const HubspotProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [isHubspotEnabled, setIsHubspotEnabled] = useState(false)
    const location = useLocation()
    const cleanupTimeout = useRef<NodeJS.Timeout>()
    const scriptLoadingRef = useRef(false)

    const forceCleanup = () => {
        try {
            // Remove all Hubspot-related scripts first
            const scripts = document.getElementsByTagName('script')
            Array.from(scripts).forEach((script) => {
                if (
                    script.src.includes('hubspot') ||
                    script.src.includes('hs-scripts') ||
                    script.src.includes('hsleadflows') ||
                    script.src.includes('hs-analytics')
                ) {
                    script.parentNode?.removeChild(script)
                }
            })

            // Remove all hubspot iframes
            const iframes = document.getElementsByTagName('iframe')
            Array.from(iframes).forEach((iframe) => {
                if (
                    iframe.src.includes('hubspot') ||
                    iframe.id.includes('hs')
                ) {
                    iframe.parentNode?.removeChild(iframe)
                }
            })

            // Clear cookies
            document.cookie.split(';').forEach((c) => {
                if (
                    c.toLowerCase().includes('hs') ||
                    c.toLowerCase().includes('hubspot')
                ) {
                    const cookieName = c.split('=')[0].trim()
                    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
                }
            })

            // Clear localStorage and sessionStorage
            ;['localStorage', 'sessionStorage'].forEach((storageType) => {
                Object.keys(
                    window[storageType as keyof typeof window] || {}
                ).forEach((key) => {
                    if (
                        key.toLowerCase().includes('hs') ||
                        key.toLowerCase().includes('hubspot')
                    ) {
                        window[storageType as keyof typeof window]?.removeItem(
                            key
                        )
                    }
                })
            })

            // Remove any remaining elements
            removeHubspotScripts()

            // Reset all Hubspot-related variables
            const hubspotVars = [
                '_hsq',
                'hbspt',
                'hsVars',
                'hsq',
                '_hsp',
                '_hssc',
                '_hstc',
                'hubspotutk',
                'leadflows',
            ]
            hubspotVars.forEach((varName) => {
                try {
                    if ((window as any)[varName]) {
                        ;(window as any)[varName] = null
                    }
                } catch (e) {
                    console.debug(
                        `Could not nullify Hubspot variable: ${varName}`
                    )
                }
            })

            // Reset Hubspot queue
            ;(window as any)._hsq = []
            scriptLoadingRef.current = false
        } catch (error) {
            console.debug('Error during Hubspot cleanup:', error)
        }
    }

    const loadHubspotScript = () => {
        if (scriptLoadingRef.current) return
        scriptLoadingRef.current = true

        if (cleanupTimeout.current) {
            clearTimeout(cleanupTimeout.current)
        }

        forceCleanup() // Clean everything first

        const script = document.createElement('script')
        script.id = 'hs-script-loader'
        script.type = 'text/javascript'
        script.async = true
        script.src = `//js.hs-scripts.com/${process.env.REACT_APP_HUBSPOT_TRACKINGID}.js`
        document.head.appendChild(script)
    }

    const enableHubspot = () => {
        if (!isHubspotEnabled) {
            setIsHubspotEnabled(true)
            loadHubspotScript()
        }
    }

    const disableHubspot = () => {
        setIsHubspotEnabled(false)
        forceCleanup()

        // Set a timeout to do another cleanup pass
        cleanupTimeout.current = setTimeout(forceCleanup, 500)
    }

    useEffect(() => {
        // Only disable Hubspot for accounts-related routes
        const isAccountsRoute =
            location.pathname.includes('/accounts') ||
            location.pathname.includes('/signin') ||
            location.pathname.includes('/signup')

        if (!isAccountsRoute && !isHubspotEnabled) {
            enableHubspot()
        } else if (isAccountsRoute && isHubspotEnabled) {
            disableHubspot()
        }

        return () => {
            if (cleanupTimeout.current) {
                clearTimeout(cleanupTimeout.current)
            }
            if (isAccountsRoute) {
                forceCleanup()
            }
        }
    }, [location.pathname, isHubspotEnabled])

    return (
        <HubspotContext.Provider
            value={{ isHubspotEnabled, enableHubspot, disableHubspot }}
        >
            {children}
        </HubspotContext.Provider>
    )
}

export const useHubspot = () => {
    const context = useContext(HubspotContext)
    if (context === undefined) {
        throw new Error('useHubspot must be used within a HubspotProvider')
    }
    return context
}
